import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { getSearchBarTheme } from '../theme/themeStyles';

interface SearchResult {
  display_name: string;
  lat: string;
  lon: string;
  boundingbox: string[];
  // For coordinate searches: store the original searched coordinates so the marker lands
  // on the user's typed point (not the snapped Nominatim address).
  originalLat?: number;
  originalLon?: number;
}

interface SearchBarProps {
  /** Called when the user picks a result. `lat`/`lon` are WGS-84; the parent should pan the
   *  map and (optionally) drop a marker — see `selectSearchLocation` exposed by `MapView`. */
  onLocationSelect: (lat: number, lon: number, name: string) => void;
  /** Optional wrapper class for positioning/sizing (the dropdown is anchored to this box). */
  className?: string;
  /** Optional override for the `<input>` styling (defaults to the top-right white pill). */
  inputClassName?: string;
  /** Custom placeholder. */
  placeholder?: string;
}

/**
 * Inline place / coordinate search powered by OSM Nominatim, biased to the Philippines.
 */
export default function SearchBar({
  onLocationSelect,
  className,
  inputClassName,
  placeholder = 'Search place or coordinates (e.g. 14.5995, 120.9842)',
}: SearchBarProps) {
  const { isDark } = useTheme();
  const sb = getSearchBarTheme(isDark);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isCoordinatePattern = (query: string): { lat: number; lon: number } | null => {
    const trimmed = query.trim();

    const commaPattern = /^(-?\d+\.?\d*)\s*[,，]\s*(-?\d+\.?\d*)$/;
    const commaMatch = trimmed.match(commaPattern);
    if (commaMatch) {
      const lat = parseFloat(commaMatch[1]);
      const lon = parseFloat(commaMatch[2]);
      if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { lat, lon };
      }
    }

    const parts = trimmed.split(/\s+/);
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lon = parseFloat(parts[1]);
      if (!Number.isNaN(lat) && !Number.isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        return { lat, lon };
      }
    }

    return null;
  };

  const reverseGeocode = async (lat: number, lon: number) => {
    setIsSearching(true);

    const exactCoordinateResult: SearchResult = {
      display_name: `${lat}, ${lon}`,
      lat: lat.toString(),
      lon: lon.toString(),
      boundingbox: [],
      originalLat: lat,
      originalLon: lon,
    };

    const results: SearchResult[] = [exactCoordinateResult];

    try {
      const reverseResponse = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`
      );
      const reverseData = await reverseResponse.json();

      if (reverseData && reverseData.display_name) {
        results.push({
          display_name: reverseData.display_name,
          lat: reverseData.lat || lat.toString(),
          lon: reverseData.lon || lon.toString(),
          boundingbox: reverseData.boundingbox || [],
        });
      }

      if (reverseData && reverseData.address) {
        const address = reverseData.address;
        const locationName =
          address.city || address.town || address.village || address.municipality || address.province || '';

        if (locationName) {
          try {
            const searchResponse = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationName)}&countrycodes=ph&limit=5&addressdetails=1`
            );
            const searchData = await searchResponse.json();

            if (Array.isArray(searchData) && searchData.length > 0) {
              searchData.forEach((place: any) => {
                const placeLat = parseFloat(place.lat);
                const placeLon = parseFloat(place.lon);
                const distance = Math.sqrt(Math.pow(placeLat - lat, 2) + Math.pow(placeLon - lon, 2)) * 111;

                const isDuplicate = results.some(
                  (r) =>
                    r.display_name === place.display_name ||
                    (Math.abs(parseFloat(r.lat) - placeLat) < 0.001 &&
                      Math.abs(parseFloat(r.lon) - placeLon) < 0.001)
                );

                if (!isDuplicate && distance < 2) {
                  results.push({
                    display_name: place.display_name,
                    lat: place.lat,
                    lon: place.lon,
                    boundingbox: place.boundingbox || [],
                  });
                }
              });
            }
          } catch {
            // Nearby-places enrichment is best-effort.
          }
        }
      }

      setSearchResults(results);
      setShowSearchResults(true);
    } catch (error) {
      console.error('[SearchBar] Reverse geocoding error:', error);
      setSearchResults(results);
      setShowSearchResults(true);
    } finally {
      setIsSearching(false);
    }
  };

  const searchLocation = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=ph&limit=5&addressdetails=1`
      );
      const data = await response.json();
      setSearchResults(Array.isArray(data) ? data : []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('[SearchBar] Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        const coordinates = isCoordinatePattern(searchQuery);
        if (coordinates) {
          reverseGeocode(coordinates.lat, coordinates.lon);
        } else {
          searchLocation(searchQuery);
        }
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    if (!showSearchResults) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSearchResults]);

  const selectSearchResult = (result: SearchResult) => {
    const lat = result.originalLat !== undefined ? result.originalLat : parseFloat(result.lat);
    const lon = result.originalLon !== undefined ? result.originalLon : parseFloat(result.lon);

    let displayName = result.display_name;
    if (result.originalLat !== undefined && result.originalLon !== undefined) {
      displayName = `${result.display_name} (${result.originalLat}, ${result.originalLon})`;
    }

    try {
      onLocationSelect(lat, lon, displayName);
    } catch (error) {
      console.error('[SearchBar] Error calling onLocationSelect:', error);
    }

    setSearchQuery(result.display_name);
    setShowSearchResults(false);
  };

  const wrapperClassName = className ?? 'relative w-full';
  const finalInputClassName = inputClassName ?? sb.input;

  return (
    <div ref={containerRef} className={wrapperClassName}>
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={placeholder}
        aria-label="Search place or coordinates (Philippines)"
        className={finalInputClassName}
        onFocus={() => {
          if (searchResults.length > 0) setShowSearchResults(true);
        }}
      />
      <i
        className={`ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-base pointer-events-none z-10 ${sb.icon}`}
        aria-hidden
      />

      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 z-10">
        {isSearching && (
          <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${sb.spinner}`} aria-hidden />
        )}
        {!!searchQuery && !isSearching && (
          <button
            type="button"
            onClick={() => {
              setSearchQuery('');
              setSearchResults([]);
              setShowSearchResults(false);
            }}
            aria-label="Clear search"
            className={sb.clearBtn}
          >
            <i className="ri-close-line text-sm" aria-hidden />
          </button>
        )}
      </div>

      {showSearchResults && searchResults.length > 0 && (
        <div className={sb.resultsPanel}>
          {searchResults.map((result, index) => (
            <button
              key={index}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selectSearchResult(result);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              className={sb.resultBtn}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className={sb.resultTitle}>
                    {result.display_name.split(',')[0]}
                  </div>
                  <div className={sb.resultSub}>{result.display_name}</div>
                  {result.originalLat !== undefined && result.originalLon !== undefined && (
                    <div className={sb.resultExact}>
                      Exact coordinates: {result.originalLat}, {result.originalLon}
                    </div>
                  )}
                </div>
                {index === 0 && result.originalLat !== undefined && (
                  <span className={sb.exactBadge}>Exact</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
